/**
 * Dashboard Page
 * ==============
 * Analytics dashboard with live stats, spending charts, and category breakdown.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import Layout from '../../components/layout/Layout';
import { useDashboardStats } from '../../hooks/useDashboardStats';
import StatsCards from './components/StatsCards';
import PeriodSelector from './components/PeriodSelector';
import SpendingChart from './components/SpendingChart';
import CategoryChartsGrid from './components/CategoryChartsGrid';
import './Dashboard.scss';

/** Convert "YYYY-MM" to "YYYY-MM-DD" (first day of month) */
const monthToStartDate = (ym: string): string => `${ym}-01`;

/** Convert "YYYY-MM" to "YYYY-MM-DD" (last day of month) */
const monthToEndDate = (ym: string): string => {
    const [y, m] = ym.split('-').map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    return `${ym}-${String(lastDay).padStart(2, '0')}`;
};

const PERIOD_TO_GROUP: Record<string, string> = {
    '7d': 'day',
    '30d': 'day',
    '90d': 'month',
    '1y': 'month',
    'all': 'month',
};

const Dashboard: React.FC = () => {
    const [period, setPeriod] = useState('all');
    const [startMonth, setStartMonth] = useState('');
    const [endMonth, setEndMonth] = useState('');
    const rangeInitialized = useRef(false);

    // Determine what to send to API
    const isPreset = period !== 'all';
    const groupBy = useMemo(() => PERIOD_TO_GROUP[period] || 'day', [period]);

    const apiStartDate = !isPreset && startMonth ? monthToStartDate(startMonth) : undefined;
    const apiEndDate = !isPreset && endMonth ? monthToEndDate(endMonth) : undefined;

    const { data, loading } = useDashboardStats(
        isPreset ? period : '30d',
        groupBy,
        apiStartDate,
        apiEndDate,
    );

    // Initialize month dropdowns from API date_range on first load
    useEffect(() => {
        if (rangeInitialized.current || !data?.date_range) return;
        const { min_month, max_month } = data.date_range;
        if (min_month && max_month) {
            setStartMonth(min_month);
            setEndMonth(max_month);
            rangeInitialized.current = true;
        }
    }, [data?.date_range]);

    const handlePeriodChange = (value: string) => {
        setPeriod(value);
        // When switching to "All", reset to full range from API
        if (value === 'all' && data?.date_range) {
            const { min_month, max_month } = data.date_range;
            if (min_month) setStartMonth(min_month);
            if (max_month) setEndMonth(max_month);
        }
    };

    const handleStartMonthChange = (m: string) => {
        setStartMonth(m);
        setPeriod('all');
    };

    const handleEndMonthChange = (m: string) => {
        setEndMonth(m);
        setPeriod('all');
    };

    return (
        <Layout pageTitle="Dashboard" pageDescription="Invoice verification and matching overview">
            <div className="dashboard">
                <StatsCards counts={data?.counts ?? null} loading={loading} />

                <PeriodSelector
                    period={period}
                    onPeriodChange={handlePeriodChange}
                    startMonth={startMonth}
                    endMonth={endMonth}
                    onStartMonthChange={handleStartMonthChange}
                    onEndMonthChange={handleEndMonthChange}
                    minMonth={data?.date_range?.min_month ?? null}
                    maxMonth={data?.date_range?.max_month ?? null}
                />

                <SpendingChart data={data?.spending_by_date ?? []} loading={loading} />

                <CategoryChartsGrid categories={data?.spending_by_category ?? []} loading={loading} />

                <div className="dashboard__section">
                    <h3 className="dashboard__section-title">Quick Actions</h3>
                    <div className="dashboard__actions">
                        <a href="/rfq-invoices" className="dashboard__action-card">
                            <div className="dashboard__action-content">
                                <h4>View RFQ Invoices</h4>
                                <p>Check invoices from your finalized RFQs</p>
                            </div>
                        </a>
                        <a href="/bulk-upload" className="dashboard__action-card">
                            <div className="dashboard__action-content">
                                <h4>Bulk Upload</h4>
                                <p>Upload invoices and quotes for matching</p>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Dashboard;
